import React, { useState, useEffect, useCallback } from 'react'
import {
  Car, Plus, Search, Filter, X, ChevronRight, Upload, Edit2, LogOut as ExitIcon,
  RefreshCw, AlertTriangle, FileText, TrendingUp, Target, BarChart3, Trash2, CheckSquare
} from 'lucide-react'
import ImportModal from '../../../components/shared/ImportModal'
import ImportIAModal from '../../../components/shared/ImportIAModal'
import { ScoreBadge, ScoreDetail } from '../../../components/shared/ScoreBadge'

const API_URL = process.env.REACT_APP_BACKEND_URL
const TYPES_VEHICULE = ['VL', 'utilitaire', 'PL', '2_roues', 'engin']
const TYPES_ASSURANCE = ['rc', 'dta', 'tous_risques']
const LABELS_TYPE = { VL: 'VL', utilitaire: 'Utilitaire', PL: 'Poids Lourd', '2_roues': '2 Roues', engin: 'Engin' }
const LABELS_ASSURANCE = { rc: 'RC', dta: 'DTA', tous_risques: 'Tous risques' }

export default function VehiculesPage({ tenants, readOnly = false, forceTenantId = null }) {
  const [vehicules, setVehicules] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filterTenant, setFilterTenant] = useState(forceTenantId || '')
  const [filterStatut, setFilterStatut] = useState('actif')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState(null)
  const [showDetail, setShowDetail] = useState(null)
  const [detailData, setDetailData] = useState(null)
  const [showImport, setShowImport] = useState(false)
  const [showImportIA, setShowImportIA] = useState(false)
  const [analytics, setAnalytics] = useState(null)
  const [scores, setScores] = useState({})
  const [selectedIds, setSelectedIds] = useState([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [bulkProgress, setBulkProgress] = useState(null)

  // Force tenant filter when in client context
  useEffect(() => {
    if (forceTenantId) setFilterTenant(forceTenantId)
  }, [forceTenantId])

  const fetchVehicules = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (filterTenant) params.set('tenant_id', filterTenant)
      if (filterStatut) params.set('statut', filterStatut)
      if (search) params.set('search', search)
      const res = await fetch(`${API_URL}/api/vehicules?${params}`)
      if (res.ok) {
        const d = await res.json()
        setVehicules(d.vehicules || [])
      }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [filterTenant, filterStatut, search])

  useEffect(() => { fetchVehicules() }, [fetchVehicules])

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const params = new URLSearchParams()
        if (filterTenant) params.set('tenant_id', filterTenant)
        const endpoint = readOnly ? `/api/analytics/vehicules/daf/${filterTenant || ''}` : `/api/analytics/vehicules?${params}`
        if (readOnly && !filterTenant) return
        const res = await fetch(`${API_URL}${endpoint}`)
        if (res.ok) setAnalytics(await res.json())
      } catch (e) { console.error(e) }
    }
    const fetchScores = async () => {
      try {
        const params = new URLSearchParams()
        if (filterTenant) params.set('tenant_id', filterTenant)
        const res = await fetch(`${API_URL}/api/scoring/vehicules?${params}`)
        if (res.ok) {
          const d = await res.json()
          const map = {}
          for (const s of d.scores || []) map[s.entity_id] = s
          setScores(map)
        }
      } catch (e) { console.error(e) }
    }
    fetchAnalytics()
    fetchScores()
  }, [filterTenant, readOnly])

  const fetchDetail = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/vehicules/${id}`)
      if (res.ok) setDetailData(await res.json())
    } catch (e) { console.error(e) }
  }

  useEffect(() => {
    if (showDetail) fetchDetail(showDetail)
  }, [showDetail])

  return (
    <div className="p-6 space-y-5 max-w-[1400px]" data-testid="vehicules-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800" data-testid="vehicules-title">Parc Véhicules</h1>
          <p className="text-sm text-slate-500">Gestion du parc de véhicules assurés</p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImport(true)} className="flex items-center gap-1.5 text-sm px-3 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-testid="vehicules-import-btn">
              <Upload size={14} /> Import
            </button>
            <button onClick={() => setShowImportIA(true)} className="flex items-center gap-1.5 text-sm px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors" data-testid="vehicules-import-ia-btn">
              <Upload size={14} /> Import IA
            </button>
            <button onClick={() => { setEditing(null); setShowForm(true) }} className="flex items-center gap-1.5 text-sm px-3 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700 transition-colors" data-testid="vehicules-add-btn">
              <Plus size={14} /> Ajouter
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher (immat, marque, modèle)..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg"
            data-testid="vehicules-search"
          />
        </div>
        {tenants?.length > 0 && !forceTenantId && (
          <select value={filterTenant} onChange={e => setFilterTenant(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="vehicules-filter-tenant">
            <option value="">Tous les clients</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
          </select>
        )}
        <select value={filterStatut} onChange={e => setFilterStatut(e.target.value)} className="text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="vehicules-filter-statut">
          <option value="">Tous statuts</option>
          <option value="actif">Actif</option>
          <option value="sorti">Sorti</option>
        </select>
        <span className="text-xs text-slate-400">{vehicules.length} véhicule(s)</span>
      </div>

      {/* Analytics block */}
      {analytics && (analytics.nb_vehicules_sinistres > 0 || analytics.alertes?.length > 0) && (
        <VehiculeAnalyticsBlock data={analytics} readOnly={readOnly} />
      )}

      {/* Bulk action bar */}
      {selectedIds.length > 0 && !readOnly && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-3 space-y-2" data-testid="bulk-action-bar">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-indigo-700">{selectedIds.length} véhicule(s) sélectionné(s)</span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkAction('sortir_parc')}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-50"
                data-testid="bulk-sortir-btn"
              >
                <ExitIcon size={12} /> Sortir du parc ({selectedIds.length})
              </button>
              <button
                onClick={() => handleBulkAction('supprimer')}
                disabled={bulkLoading}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
                data-testid="bulk-delete-btn"
              >
                <Trash2 size={12} /> Supprimer ({selectedIds.length})
              </button>
              <button onClick={() => setSelectedIds([])} className="text-xs text-slate-500 hover:text-slate-700 ml-2">Annuler</button>
            </div>
          </div>
          {bulkProgress && (
            <div className="space-y-1" data-testid="bulk-progress">
              <div className="w-full bg-indigo-200 rounded-full h-2">
                <div
                  className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${Math.round((bulkProgress.done / bulkProgress.total) * 100)}%` }}
                />
              </div>
              <p className="text-xs text-indigo-600">
                {bulkProgress.done} / {bulkProgress.total} traité(s)
                {bulkProgress.errors > 0 && <span className="text-red-500 ml-2">{bulkProgress.errors} erreur(s)</span>}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-slate-400" /></div>
        ) : vehicules.length === 0 ? (
          <div className="text-center py-12">
            <Car className="w-10 h-10 mx-auto text-slate-300 mb-3" />
            <p className="text-sm text-slate-500 font-medium">Aucun véhicule trouvé</p>
            <p className="text-xs text-slate-400 mt-1">{!readOnly ? "Ajoutez votre premier véhicule ou importez un fichier" : "Le parc véhicules sera disponible après création"}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="vehicules-table">
              <thead>
                <tr className="bg-slate-50/80 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">
                  {!readOnly && (
                    <th className="px-3 py-2.5 text-center w-8">
                      <input
                        type="checkbox"
                        checked={selectedIds.length > 0 && selectedIds.length === vehicules.length}
                        onChange={e => setSelectedIds(e.target.checked ? vehicules.map(v => v.id) : [])}
                        className="w-3.5 h-3.5 rounded border-slate-300"
                        data-testid="select-all-checkbox"
                      />
                    </th>
                  )}
                  <th className="px-4 py-2.5 text-left">Immatriculation</th>
                  <th className="px-4 py-2.5 text-left">Marque / Modèle</th>
                  <th className="px-4 py-2.5 text-left">Type</th>
                  <th className="px-4 py-2.5 text-left">Assurance</th>
                  {!forceTenantId && <th className="px-4 py-2.5 text-left">Client</th>}
                  <th className="px-4 py-2.5 text-left">Police</th>
                  <th className="px-4 py-2.5 text-center">Statut</th>
                  <th className="px-4 py-2.5 text-center">Score</th>
                  <th className="px-4 py-2.5 text-left">Date entrée</th>
                  {!readOnly && <th className="px-4 py-2.5 text-center">Actions</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {vehicules.map((v, i) => (
                  <tr key={v.id} className={`hover:bg-slate-50 transition-colors cursor-pointer ${selectedIds.includes(v.id) ? 'bg-indigo-50/50' : ''}`} onClick={() => setShowDetail(v.id)} data-testid={`vehicule-row-${i}`}>
                    {!readOnly && (
                      <td className="px-3 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(v.id)}
                          onChange={e => {
                            if (e.target.checked) setSelectedIds(prev => [...prev, v.id])
                            else setSelectedIds(prev => prev.filter(id => id !== v.id))
                          }}
                          className="w-3.5 h-3.5 rounded border-slate-300"
                          data-testid={`vehicule-checkbox-${i}`}
                        />
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-bold text-slate-800 tracking-wide">{v.immatriculation}</td>
                    <td className="px-4 py-2.5 text-slate-600">{v.marque} {v.modele}</td>
                    <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-[10px] font-medium bg-slate-100 text-slate-600 rounded">{LABELS_TYPE[v.type_vehicule] || v.type_vehicule}</span></td>
                    <td className="px-4 py-2.5"><span className="px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded">{LABELS_ASSURANCE[v.type_assurance] || v.type_assurance}</span></td>
                    {!forceTenantId && <td className="px-4 py-2.5 text-slate-600">{v.tenants?.nom || '—'}</td>}
                    <td className="px-4 py-2.5 text-slate-500">{v.polices?.numero_police || '—'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${v.statut === 'actif' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{v.statut}</span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      {scores[v.id] ? (
                        <ScoreBadge score={scores[v.id].score} niveau={scores[v.id].niveau} size="xs" />
                      ) : <span className="text-[10px] text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-2.5 text-slate-500">{v.date_entree || '—'}</td>
                    {!readOnly && (
                      <td className="px-4 py-2.5 text-center" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => { setEditing(v); setShowForm(true) }} className="p-1 hover:bg-slate-100 rounded" data-testid={`vehicule-edit-${i}`}><Edit2 size={12} className="text-slate-500" /></button>
                          {v.statut === 'actif' && (
                            <button onClick={() => handleSortie(v.id)} className="p-1 hover:bg-red-50 rounded" data-testid={`vehicule-sortie-${i}`}><ExitIcon size={12} className="text-red-400" /></button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      {showForm && !readOnly && (
        <VehiculeForm
          tenants={tenants}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null) }}
          onSave={() => { setShowForm(false); setEditing(null); fetchVehicules() }}
        />
      )}

      {/* Import modal V2 */}
      {showImport && !readOnly && (
        <ImportModal
          entityType="vehicules"
          tenants={tenants}
          onClose={() => setShowImport(false)}
          onDone={() => { setShowImport(false); fetchVehicules() }}
        />
      )}

      {/* Import IA modal */}
      <ImportIAModal
        isOpen={showImportIA}
        onClose={() => setShowImportIA(false)}
        tenantId={forceTenantId || filterTenant}
        policeId={null}
        importType="vehicules"
        onSuccess={() => { setShowImportIA(false); fetchVehicules() }}
      />

      {/* Detail panel */}
      {showDetail && detailData && (
        <DetailPanel data={detailData} type="vehicule" onClose={() => { setShowDetail(null); setDetailData(null) }} />
      )}
    </div>
  )

  async function handleBulkAction(action) {
    const actionLabel = action === 'sortir_parc' ? 'sortir du parc' : 'supprimer définitivement'
    const confirmed = window.confirm(
      `Voulez-vous ${actionLabel} ${selectedIds.length} véhicule(s) ?\n\n${
        action === 'supprimer' ? 'Cette action est irréversible.' : 'Les véhicules seront marqués comme sortis.'
      }`
    )
    if (!confirmed) return

    setBulkLoading(true)
    setBulkProgress({ done: 0, total: selectedIds.length, errors: 0 })

    // Split into batches of 200 IDs for frontend → backend
    const batchSize = 200
    const allIds = [...selectedIds]
    let totalDone = 0
    let totalErrors = 0

    try {
      for (let i = 0; i < allIds.length; i += batchSize) {
        const batch = allIds.slice(i, i + batchSize)
        try {
          const res = await fetch(`${API_URL}/api/vehicules/bulk-action`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: batch, action })
          })
          const data = await res.json()
          if (data.success) {
            totalDone += data.traites || 0
            totalErrors += data.erreurs || 0
          } else {
            totalErrors += batch.length
          }
        } catch {
          totalErrors += batch.length
        }
        setBulkProgress({ done: totalDone, total: allIds.length, errors: totalErrors })
      }

      alert(`${totalDone} véhicule(s) traité(s) avec succès.${totalErrors > 0 ? ` ${totalErrors} erreur(s).` : ''}`)
      setSelectedIds([])
      fetchVehicules()
    } catch (e) {
      alert('Erreur lors du traitement')
    } finally {
      setBulkLoading(false)
      setBulkProgress(null)
    }
  }

  async function handleSortie(id) {
    if (!window.confirm('Confirmer la sortie de ce véhicule du parc ?')) return
    try {
      const res = await fetch(`${API_URL}/api/vehicules/${id}/sortie`, { method: 'PUT' })
      if (res.ok) fetchVehicules()
    } catch (e) { console.error(e) }
  }
}

// ── Vehicle Form ──────────────────────────────────────────────────────────
function VehiculeForm({ tenants, editing, onClose, onSave }) {
  const [form, setForm] = useState({
    tenant_id: editing?.tenant_id || (tenants?.[0]?.id || ''),
    police_id: editing?.police_id || '',
    immatriculation: editing?.immatriculation || '',
    marque: editing?.marque || '',
    modele: editing?.modele || '',
    type_vehicule: editing?.type_vehicule || 'VL',
    type_assurance: editing?.type_assurance || 'rc',
    date_entree: editing?.date_entree || '',
    notes: editing?.notes || '',
  })
  const [polices, setPolices] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (form.tenant_id) {
      fetch(`${API_URL}/api/polices?tenant_id=${form.tenant_id}`)
        .then(r => r.json())
        .then(d => setPolices(Array.isArray(d) ? d : d.polices || []))
        .catch(() => {})
    }
  }, [form.tenant_id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const url = editing ? `${API_URL}/api/vehicules/${editing.id}` : `${API_URL}/api/vehicules`
      const method = editing ? 'PUT' : 'POST'
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (res.ok) { onSave() }
      else {
        const d = await res.json()
        setError(d.detail || 'Erreur lors de la sauvegarde')
      }
    } catch (e) { setError(e.message) }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="vehicule-form-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-800">{editing ? 'Modifier le véhicule' : 'Ajouter un véhicule'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="px-3 py-2 bg-red-50 text-red-700 text-xs rounded-lg">{error}</div>}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Client *</label>
              <select value={form.tenant_id} onChange={e => setForm({ ...form, tenant_id: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" required data-testid="vehicule-form-tenant">
                {tenants?.map(t => <option key={t.id} value={t.id}>{t.nom}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Immatriculation *</label>
              <input value={form.immatriculation} onChange={e => setForm({ ...form, immatriculation: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="AB-123-CD" required data-testid="vehicule-form-immat" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Marque</label>
              <input value={form.marque} onChange={e => setForm({ ...form, marque: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="Renault" data-testid="vehicule-form-marque" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Modèle</label>
              <input value={form.modele} onChange={e => setForm({ ...form, modele: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" placeholder="Master" data-testid="vehicule-form-modele" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Type véhicule</label>
              <select value={form.type_vehicule} onChange={e => setForm({ ...form, type_vehicule: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="vehicule-form-type">
                {TYPES_VEHICULE.map(t => <option key={t} value={t}>{LABELS_TYPE[t] || t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Type assurance</label>
              <select value={form.type_assurance} onChange={e => setForm({ ...form, type_assurance: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="vehicule-form-assurance">
                {TYPES_ASSURANCE.map(t => <option key={t} value={t}>{LABELS_ASSURANCE[t] || t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Police liée</label>
              <select value={form.police_id} onChange={e => setForm({ ...form, police_id: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="vehicule-form-police">
                <option value="">Aucune</option>
                {polices.map(p => <option key={p.id} value={p.id}>{p.numero_police} — {p.libelle}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Date entrée</label>
              <input type="date" value={form.date_entree} onChange={e => setForm({ ...form, date_entree: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2" data-testid="vehicule-form-date" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
            <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="w-full text-sm border border-slate-200 rounded-lg px-3 py-2 h-16" data-testid="vehicule-form-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">Annuler</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50" data-testid="vehicule-form-submit">
              {saving ? 'Enregistrement...' : editing ? 'Enregistrer' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Detail Panel ──────────────────────────────────────────────────────────
function DetailPanel({ data, type, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/30 flex items-end sm:items-center justify-center z-50" data-testid="detail-panel">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg mx-0 sm:mx-4 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-slate-800">
            {type === 'vehicule' ? data.immatriculation : data.nom}
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-4 text-xs">
          {type === 'vehicule' && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <InfoRow label="Marque" value={data.marque} />
                <InfoRow label="Modèle" value={data.modele} />
                <InfoRow label="Type" value={LABELS_TYPE[data.type_vehicule] || data.type_vehicule} />
                <InfoRow label="Assurance" value={LABELS_ASSURANCE[data.type_assurance] || data.type_assurance} />
                <InfoRow label="Police" value={data.polices?.numero_police} />
                <InfoRow label="Client" value={data.tenants?.nom} />
                <InfoRow label="Statut" value={data.statut} />
                <InfoRow label="Date entrée" value={data.date_entree} />
                {data.date_sortie && <InfoRow label="Date sortie" value={data.date_sortie} />}
              </div>
              {data.notes && <div className="p-3 bg-slate-50 rounded-lg"><p className="text-slate-600">{data.notes}</p></div>}
            </>
          )}
          {data.sinistres?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">Sinistres rattachés ({data.sinistres.length})</p>
              <div className="space-y-1.5">
                {data.sinistres.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg">
                    <div>
                      <p className="font-medium text-slate-700">{s.numero_sinistre}</p>
                      <p className="text-slate-500">{s.nature_sinistre || s.type_sinistre}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-slate-700">{s.montant_dommages_estime ? `${Number(s.montant_dommages_estime).toLocaleString('fr-FR')} €` : '—'}</p>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${s.statut === 'clos' ? 'bg-emerald-50 text-emerald-700' : 'bg-blue-50 text-blue-700'}`}>{s.statut}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {(!data.sinistres || data.sinistres.length === 0) && (
            <p className="text-center text-slate-400 py-4">Aucun sinistre rattaché</p>
          )}
        </div>
      </div>
    </div>
  )
}

function VehiculeAnalyticsBlock({ data, readOnly }) {
  const fmt = (n) => n != null ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n) : '—'

  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm" data-testid="vehicule-analytics">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <BarChart3 size={16} className="text-blue-600" />
        <h2 className="text-sm font-semibold text-slate-700">Analytique sinistralité véhicules</h2>
        <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{data.seuil_alerte}</span>
      </div>
      <div className="p-5 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Véhicules sinistrés</p>
            <p className="text-xl font-bold text-slate-800">{data.nb_vehicules_sinistres} <span className="text-xs font-normal text-slate-400">/ {data.nb_vehicules_total}</span></p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Sinistres rattachés</p>
            <p className="text-xl font-bold text-slate-800">{data.nb_sinistres_rattaches}</p>
            {data.nb_sinistres_sans_vehicule > 0 && <p className="text-[10px] text-slate-400">{data.nb_sinistres_sans_vehicule} non rattachés</p>}
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Coût total</p>
            <p className="text-xl font-bold text-slate-800">{fmt(data.cout_total)}</p>
          </div>
          <div className="p-3 bg-slate-50 rounded-lg">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Coût moyen / véhicule</p>
            <p className="text-xl font-bold text-slate-800">{fmt(data.cout_moyen_par_vehicule)}</p>
          </div>
        </div>

        {/* Alerts */}
        {data.alertes?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Alertes détectées</p>
            <div className="space-y-1.5">
              {data.alertes.slice(0, 5).map((a, i) => (
                <div key={i} className={`flex items-start gap-2 p-2.5 rounded-lg ${a.niveau === 'critical' ? 'bg-red-50' : 'bg-amber-50'}`} data-testid={`vehicule-alert-${i}`}>
                  <AlertTriangle size={14} className={`mt-0.5 shrink-0 ${a.niveau === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${a.niveau === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                        {a.type === 'frequence_anormale' ? 'FRÉQUENCE' : 'COÛT'}
                      </span>
                      <span className="text-xs font-semibold text-slate-700">{a.entity_label}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 mt-0.5">{a.explication}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Concentration */}
        {data.concentration?.concentration_detected && (
          <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100" data-testid="vehicule-concentration">
            <div className="flex items-center gap-2 mb-1">
              <Target size={14} className="text-indigo-600" />
              <span className="text-xs font-semibold text-indigo-700">Concentration détectée</span>
            </div>
            <p className="text-[11px] text-indigo-600">{data.concentration.explication}</p>
          </div>
        )}

        {/* Top vehicules */}
        {data.top_vehicules?.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-600 mb-2">Top véhicules sinistrés</p>
            <div className="space-y-1">
              {data.top_vehicules.slice(0, 5).map((v, i) => (
                <div key={v.vehicule_id} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg" data-testid={`top-vehicule-${i}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-[10px] font-bold text-slate-400 w-4">{i + 1}</span>
                    <span className="text-xs font-bold text-slate-800 tracking-wide">{v.immatriculation}</span>
                    {v.marque && <span className="text-[10px] text-slate-500">{v.marque} {v.modele}</span>}
                  </div>
                  <div className="flex items-center gap-4 text-xs text-slate-600">
                    <span>{v.nb_sinistres} sin.</span>
                    <span className="font-semibold text-slate-800">{fmt(v.cout_total)}</span>
                    <span className="text-[10px] text-slate-400">{v.nb_ouverts} ouv. / {v.nb_clos} clos</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-xs font-medium text-slate-700 mt-0.5">{value || '—'}</p>
    </div>
  )
}
